import { t, tx } from "@/i18n/translate";
import type {
  ChecksAndCorrectionsPresent,
  CorrigendumPresent,
  ElectionWithPoliticalGroups,
  PoliticalGroup,
} from "@/types/generated/openapi";
import type {
  CheckboxesSubsection,
  DataEntryModel,
  DataEntrySection,
  DataEntryStructure,
  InputGridSubsection,
  InputGridSubsectionRow,
} from "@/types/types";
import { getCandidateFullName } from "@/utils/candidate";
import { formatPoliticalGroupName } from "@/utils/politicalGroup";
import { isLocalElection } from "./election";

type ModelForGSB = Extract<DataEntryModel, "DSOFirstSession" | "CSOFirstSession" | "DSONextSession" | "CSONextSession">;
type ModelForCSB = Extract<DataEntryModel, "GSB">;

const isEntryForGSB = (model: DataEntryModel): model is ModelForGSB => {
  switch (model) {
    case "DSOFirstSession":
    case "CSOFirstSession":
    case "DSONextSession":
    case "CSONextSession":
      return true;
    case "GSB":
      return false;
  }
};

const isEntryForCSB = (model: DataEntryModel): model is ModelForCSB => {
  return model === "GSB";
};

const createVotersAndVotesRows = (
  election: ElectionWithPoliticalGroups,
  model: DataEntryModel,
): InputGridSubsectionRow[] => {
  const rowsPerPoliticalGroup: InputGridSubsectionRow[] = election.political_groups.map((politicalGroup, index) => ({
    code: `E.${politicalGroup.number}`,
    path: `votes_counts.political_group_total_votes.${index}.total`,
    title: `${t("total")} ${formatPoliticalGroupName(politicalGroup)}`,
    addSeparator: index === election.political_groups.length - 1,
  }));

  return [
    {
      code: "A",
      path: "voters_counts.poll_card_count",
      title: t("voters_votes_counts.voters_counts.poll_card_count"),
      autoFocusInput: isEntryForGSB(model),
    },
    {
      code: "B",
      path: "voters_counts.proxy_certificate_count",
      title: t("voters_votes_counts.voters_counts.proxy_certificate_count"),
    },
    {
      code: "C",
      path: "voters_counts.voter_card_count",
      title: t("voters_votes_counts.voters_counts.voter_card_count"),
      isDisabled: isLocalElection(election.category),
      addSeparator: true,
    },
    {
      code: "D",
      path: "voters_counts.total_admitted_voters_count",
      title: t("voters_votes_counts.voters_counts.total_admitted_voters_count"),
      isTotal: true,
      addSeparator: true,
    },
    ...rowsPerPoliticalGroup,
    {
      code: "E",
      path: "votes_counts.total_votes_candidates_count",
      title: t("voters_votes_counts.votes_counts.total_votes_candidates_count"),
      isTotal: true,
    },
    {
      code: "F",
      path: "votes_counts.blank_votes_count",
      title: t("voters_votes_counts.votes_counts.blank_votes_count"),
    },
    {
      code: "G",
      path: "votes_counts.invalid_votes_count",
      title: t("voters_votes_counts.votes_counts.invalid_votes_count"),
      addSeparator: true,
    },
    {
      code: "H",
      path: "votes_counts.total_votes_cast_count",
      title: t("voters_votes_counts.votes_counts.total_votes_cast_count"),
      isTotal: true,
    },
  ];
};

export const createVotersAndVotesSection = (
  model: DataEntryModel,
  election: ElectionWithPoliticalGroups,
): DataEntrySection => {
  const subsections: DataEntrySection["subsections"] = [];

  if (!isEntryForGSB(model)) {
    subsections.push({
      type: "inputGrid",
      headers: [t("field"), t("counted_number"), t("description")],
      rows: [{ code: "Z", path: "number_of_voters", title: t("results.field.number_of_voters"), autoFocusInput: true }],
    });
    subsections.push({ type: "message", message: t("voters_votes_counts.number_of_votes_corrigendum") });
  }

  subsections.push({
    type: "inputGrid",
    headers: [t("field"), t("counted_number"), t("description")],
    rows: createVotersAndVotesRows(election, model),
  });

  return {
    id: "voters_votes_counts",
    title: t(`voters_votes_counts.form_title.${model}`),
    short_title: t("voters_votes_counts.short_title"),
    sectionNumber: t(`voters_votes_counts.section_number.${model}`),
    subsections,
  };
};

const createCompareVotesCastCheckboxes = (model: ModelForGSB): CheckboxesSubsection => {
  return {
    type: "checkboxes",
    title: tx(`differences_counts.compare_votes_cast_admitted_voters.title.${model}`),
    short_title: t("differences_counts.compare_votes_cast_admitted_voters.short_title"),
    error_path: "differences_counts.compare_votes_cast_admitted_voters",
    error_message: t("differences_counts.validation_error"),
    options: [
      {
        path: "differences_counts.compare_votes_cast_admitted_voters.admitted_voters_equal_votes_cast",
        label: tx("differences_counts.admitted_voters_equal_votes_cast.title"),
        short_label: t("differences_counts.admitted_voters_equal_votes_cast.short_title"),
        autoFocusInput: true,
      },
      {
        path: "differences_counts.compare_votes_cast_admitted_voters.votes_cast_greater_than_admitted_voters",
        label: tx("differences_counts.votes_cast_greater_than_admitted_voters.title"),
        short_label: t("differences_counts.votes_cast_greater_than_admitted_voters.short_title"),
      },
      {
        path: "differences_counts.compare_votes_cast_admitted_voters.votes_cast_smaller_than_admitted_voters",
        label: tx("differences_counts.votes_cast_smaller_than_admitted_voters.title"),
        short_label: t("differences_counts.votes_cast_smaller_than_admitted_voters.short_title"),
      },
    ],
  };
};

const createDifferenceAccountedForCheckboxes = (model: ModelForGSB): CheckboxesSubsection => {
  return {
    type: "checkboxes",
    title: tx(`differences_counts.difference_completely_accounted_for.title.${model}`),
    short_title: t("differences_counts.difference_completely_accounted_for.short_title"),
    error_path: "differences_counts.difference_completely_accounted_for",
    error_message: t("differences_counts.validation_error"),
    options: [
      { path: "differences_counts.difference_completely_accounted_for.yes", label: t("yes"), short_label: t("yes") },
      {
        path: "differences_counts.difference_completely_accounted_for.no",
        label: t("differences_counts.difference_completely_accounted_for.no_there_is_an_unexplained_difference"),
        short_label: t("differences_counts.difference_completely_accounted_for.no_there_is_an_unexplained_difference"),
      },
    ],
  };
};

export const createDifferencesSection = (model: DataEntryModel): DataEntrySection => {
  const differencesInputGrid: InputGridSubsection = {
    type: "inputGrid",
    headers: [t("field"), t("counted_number"), t("description")],
    rows: [
      {
        code: "I",
        path: "differences_counts.more_ballots_count",
        title: t(`differences_counts.differences_counts.more_ballots_count.${model}`),
      },
      {
        code: "J",
        path: "differences_counts.fewer_ballots_count",
        title: t(`differences_counts.differences_counts.fewer_ballots_count.${model}`),
      },
    ],
  };

  if (isEntryForCSB(model)) {
    return {
      id: "differences_counts",
      title: t("differences_counts.form_title"),
      short_title: t("differences_counts.short_title"),
      subsections: [
        {
          type: "message",
          variant: "dimmed",
          message: t("differences_counts.checkbox_message"),
        },
        differencesInputGrid,
      ],
    };
  }

  return {
    id: "differences_counts",
    title: t("differences_counts.form_title"),
    short_title: t("differences_counts.short_title"),
    sectionNumber: t(`differences_counts.section_number.${model}`),
    subsections: [
      createCompareVotesCastCheckboxes(model),
      differencesInputGrid,
      createDifferenceAccountedForCheckboxes(model),
    ],
  };
};

const aboutReportSection: DataEntrySection = {
  id: "about_report",
  title: t("about_report.form_title"),
  short_title: t("about_report.short_title"),
  subsections: [
    {
      type: "radio",
      path: "about_report.corrigendum_present",
      title: tx("about_report.corrigendum_present.title"),
      short_title: t("about_report.corrigendum_present.short_title"),
      error: t("about_report.validation_error"),
      options: [
        {
          label: tx("about_report.corrigendum_present.two_documents"),
          short_label: t("about_report.corrigendum_present.two_documents_short"),
          value: "TwoDocuments" satisfies CorrigendumPresent,
        },
        {
          label: tx("about_report.corrigendum_present.one_document"),
          short_label: t("about_report.corrigendum_present.one_document_short"),
          value: "OneDocument" satisfies CorrigendumPresent,
        },
      ],
    },
    {
      type: "radio",
      path: "about_report.checks_and_corrections_present",
      title: tx("about_report.checks_and_corrections_present.title"),
      short_title: t("about_report.checks_and_corrections_present.short_title"),
      error: t("about_report.validation_error"),
      options: [
        {
          label: t("about_report.checks_and_corrections_present.page_present"),
          short_label: t("about_report.checks_and_corrections_present.page_present"),
          value: "PagePresent" satisfies ChecksAndCorrectionsPresent,
        },
        {
          label: t("about_report.checks_and_corrections_present.page_missing"),
          short_label: t("about_report.checks_and_corrections_present.page_missing"),
          value: "PageMissing" satisfies ChecksAndCorrectionsPresent,
        },
      ],
    },
  ],
};

const checksAndCorrections: DataEntrySection = {
  id: "checks_and_corrections",
  title: t("checks_and_corrections.form_title"),
  short_title: t("checks_and_corrections.short_title"),
  disabled_when: {
    path: "about_report.checks_and_corrections_present",
    equal_to: "PageMissing" satisfies ChecksAndCorrectionsPresent,
  },
  subsections: [
    {
      type: "message",
      message: t("checks_and_corrections.form_description"),
    },
    {
      type: "heading",
      title: t("checks_and_corrections.own_initiative"),
    },
    {
      type: "checkboxes",
      title: t("checks_and_corrections.reason_investigation_own_initiative.title"),
      short_title: t("checks_and_corrections.reason_investigation_own_initiative.short_title"),
      error_path: "checks_and_corrections.reason_investigation_own_initiative",
      error_message: t("checks_and_corrections.validation_error"),
      options: [
        {
          path: "checks_and_corrections.reason_investigation_own_initiative.unaccounted_difference",
          label: t("checks_and_corrections.reason_investigation_own_initiative.unaccounted_difference"),
          short_label: t("checks_and_corrections.reason_investigation_own_initiative.unaccounted_difference"),
          autoFocusInput: true,
        },
        {
          path: "checks_and_corrections.reason_investigation_own_initiative.other_error",
          label: t("checks_and_corrections.reason_investigation_own_initiative.other_error"),
          short_label: t("checks_and_corrections.reason_investigation_own_initiative.other_error"),
        },
      ],
    },
    {
      type: "checkboxes",
      title: t("checks_and_corrections.corrected_results_own_initiative.title"),
      short_title: t("checks_and_corrections.corrected_results_own_initiative.short_title"),
      error_path: "checks_and_corrections.corrected_results_own_initiative",
      error_message: t("checks_and_corrections.validation_error"),
      options: [
        {
          path: "checks_and_corrections.corrected_results_own_initiative.no",
          label: t("checks_and_corrections.corrected_results_own_initiative.no"),
          short_label: t("checks_and_corrections.corrected_results_own_initiative.no"),
        },
        {
          path: "checks_and_corrections.corrected_results_own_initiative.yes",
          label: t("checks_and_corrections.corrected_results_own_initiative.yes"),
          short_label: t("checks_and_corrections.corrected_results_own_initiative.yes"),
        },
      ],
    },
    {
      type: "heading",
      title: t("checks_and_corrections.csb_request"),
    },
    {
      type: "checkboxes",
      title: t("checks_and_corrections.corrected_results_csb_request.title"),
      short_title: t("checks_and_corrections.corrected_results_csb_request.short_title"),
      error_path: "checks_and_corrections.corrected_results_csb_request",
      error_message: t("checks_and_corrections.validation_error"),
      options: [
        {
          path: "checks_and_corrections.corrected_results_csb_request.no",
          label: t("checks_and_corrections.corrected_results_csb_request.no"),
          short_label: t("checks_and_corrections.corrected_results_csb_request.no"),
        },
        {
          path: "checks_and_corrections.corrected_results_csb_request.yes",
          label: t("checks_and_corrections.corrected_results_csb_request.yes"),
          short_label: t("checks_and_corrections.corrected_results_csb_request.yes"),
        },
      ],
    },
  ],
};

const extraInvestigationSection: DataEntrySection = {
  id: "extra_investigation",
  title: t("extra_investigation.form_title"),
  short_title: t("extra_investigation.short_title"),
  sectionNumber: "B1-1",
  subsections: [
    {
      type: "message",
      message: t("extra_investigation.form_description"),
    },
    {
      type: "checkboxes",
      title: t("extra_investigation.extra_investigation_done.title"),
      short_title: t("extra_investigation.extra_investigation_done.short_title"),
      error_path: "extra_investigation.extra_investigation_done",
      error_message: t("extra_investigation.validation_error"),
      options: [
        {
          path: "extra_investigation.extra_investigation_done.yes",
          label: t("yes"),
          short_label: t("yes"),
          autoFocusInput: true,
        },
        {
          path: "extra_investigation.extra_investigation_done.no",
          label: t("no"),
          short_label: t("no"),
        },
      ],
    },
    {
      type: "checkboxes",
      title: t("extra_investigation.ballots_recounted_extra_investigation.title"),
      short_title: t("extra_investigation.ballots_recounted_extra_investigation.short_title"),
      error_path: "extra_investigation.ballots_recounted_extra_investigation",
      error_message: t("extra_investigation.validation_error"),
      options: [
        {
          path: "extra_investigation.ballots_recounted_extra_investigation.yes",
          label: t("yes"),
          short_label: t("yes"),
        },
        {
          path: "extra_investigation.ballots_recounted_extra_investigation.no",
          label: t("no"),
          short_label: t("no"),
        },
      ],
    },
  ],
};

const countingDifferencesPollingStation: DataEntrySection = {
  id: "counting_differences_polling_station",
  title: t("counting_differences_polling_station.form_title"),
  short_title: t("counting_differences_polling_station.short_title"),
  sectionNumber: "B1-2",
  subsections: [
    {
      type: "message",
      message: t("counting_differences_polling_station.form_description"),
    },
    {
      type: "heading",
      title: t("counting_differences_polling_station.heading_voters_and_votes"),
    },
    {
      type: "checkboxes",
      title: t("counting_differences_polling_station.difference_ballots_voters_completely_accounted_for.title"),
      short_title: t(
        "counting_differences_polling_station.difference_ballots_voters_completely_accounted_for.short_title",
      ),
      error_path: "counting_differences_polling_station.difference_ballots_voters_completely_accounted_for",
      error_message: t("counting_differences_polling_station.validation_error"),
      options: [
        {
          path: "counting_differences_polling_station.difference_ballots_voters_completely_accounted_for.yes",
          label: t("yes"),
          short_label: t("yes"),
          autoFocusInput: true,
        },
        {
          path: "counting_differences_polling_station.difference_ballots_voters_completely_accounted_for.no",
          label: t("no"),
          short_label: t("no"),
        },
      ],
    },
    {
      type: "heading",
      title: t("counting_differences_polling_station.heading_counts_of_lists"),
    },
    {
      type: "checkboxes",
      title: t("counting_differences_polling_station.difference_ballots_per_list.title"),
      short_title: t("counting_differences_polling_station.difference_ballots_per_list.short_title"),
      error_path: "counting_differences_polling_station.difference_ballots_per_list",
      error_message: t("counting_differences_polling_station.validation_error"),
      options: [
        {
          path: "counting_differences_polling_station.difference_ballots_per_list.yes",
          label: t("yes"),
          short_label: t("yes"),
        },
        {
          path: "counting_differences_polling_station.difference_ballots_per_list.no",
          label: t("no"),
          short_label: t("no"),
        },
      ],
    },
  ],
};

/**
 * Creates political group sections based on election data
 * @param election ElectionWithPoliticalGroups object
 * @returns Array of DataEntrySection objects for each political group
 */
export function createPoliticalGroupSections(election: ElectionWithPoliticalGroups): DataEntrySection[] {
  return election.political_groups.map((politicalGroup: PoliticalGroup, pgIndex: number): DataEntrySection => {
    const rows: InputGridSubsectionRow[] = [];

    // Add candidate vote fields
    politicalGroup.candidates.forEach((candidate, candidateIndex) => {
      rows.push({
        code: `${candidate.number}`,
        path: `political_group_votes.${pgIndex}.candidate_votes.${candidateIndex}.votes`,
        title: getCandidateFullName(candidate),
        autoFocusInput: candidateIndex === 0,
        addSeparator: (candidateIndex + 1) % 25 === 0 && candidateIndex + 1 !== politicalGroup.candidates.length,
      });
    });

    rows.push({
      path: `political_group_votes.${pgIndex}.total`,
      title: t("totals_list", { group_number: politicalGroup.number }),
      isListTotal: true,
    });

    const title = formatPoliticalGroupName(politicalGroup);
    return {
      id: `political_group_votes_${politicalGroup.number}`,
      title: title,
      short_title: title,
      subsections: [
        {
          type: "inputGrid",
          headers: [t("number"), t("vote_count"), t("candidate.title.singular")],
          zebra: true,
          rows,
        },
      ],
    };
  });
}

function buildDataEntryStructure(model: DataEntryModel, election: ElectionWithPoliticalGroups): DataEntryStructure {
  switch (model) {
    case "DSOFirstSession":
      return [
        aboutReportSection,
        checksAndCorrections,
        createVotersAndVotesSection(model, election),
        createDifferencesSection(model),
        ...createPoliticalGroupSections(election),
      ];
    case "CSOFirstSession":
      return [
        extraInvestigationSection,
        countingDifferencesPollingStation,
        createVotersAndVotesSection(model, election),
        createDifferencesSection(model),
        ...createPoliticalGroupSections(election),
      ];
    case "DSONextSession":
    case "CSONextSession":
    case "GSB":
      return [
        createVotersAndVotesSection(model, election),
        createDifferencesSection(model),
        ...createPoliticalGroupSections(election),
      ];
  }
}

/**
 * Returns all data entry sections.
 *
 * @param model Data entry model that determines the structure
 * @param election ElectionWithPoliticalGroups object
 * @returns Complete array of all data entry sections
 */
export function getDataEntryStructure(
  model: DataEntryModel,
  election: ElectionWithPoliticalGroups,
): DataEntryStructure {
  return buildDataEntryStructure(model, election);
}
